import { Controller } from "stimulus";
import Tribute from "tributejs";
import Trix from "trix";

const SEARCH_DEBOUNCE_MS = 175;

export default class extends Controller {
  static targets = [ "field" ]

  connect() {
    this.editor = this.fieldTarget.editor
    this.searchTimers = {}
    this.searchControllers = {}
    this.lastMentionSearchErrorAt = 0

    this.fetchUsers = this.searchValues("/business/mentions/users.json")
    this.fetchClients = this.searchValues("/business/mentions/clients.json")
    this.fetchPets = this.searchValues("/business/mentions/pets.json")

    this.initializeTribute()
    this.initializeTributeForClients()
    this.initializeTributeForPets()
  }

  disconnect() {
    this.tribute.detach(this.fieldTarget)
    Object.values(this.searchTimers).forEach(timer => clearTimeout(timer))
    Object.values(this.searchControllers).forEach(controller => controller.abort())
  }

  initializeTribute() {
    this.tribute = new Tribute({
      allowSpaces: true,
      lookup: 'name',
      values: this.fetchUsers,
    })
    this.tribute.attach(this.fieldTarget)
    this.tribute.range.pasteHtml = this._pasteHtml.bind(this)
    this.fieldTarget.addEventListener("tribute-replaced", this.replaced)
  }

  initializeTributeForClients() {
    this.tribute = new Tribute({
      trigger: '#',
      allowSpaces: true,
      lookup: 'name',
      values: this.fetchClients,
    })
    this.tribute.attach(this.fieldTarget)
    this.tribute.range.pasteHtml = this._pasteHtml.bind(this)
    this.fieldTarget.addEventListener("tribute-replaced", this.replaced)
  }

  initializeTributeForPets() {
    this.tribute = new Tribute({
      trigger: '##',
      allowSpaces: true,
      lookup: 'name',
      values: this.fetchPets,
    })
    this.tribute.attach(this.fieldTarget)
    this.tribute.range.pasteHtml = this._pasteHtml.bind(this)
    this.fieldTarget.addEventListener("tribute-replaced", this.replaced)
  }


  searchValues(endpoint) {
    return (text, callback) => {
      clearTimeout(this.searchTimers[endpoint])
      this.searchTimers[endpoint] = setTimeout(() => {
        this.performSearch(endpoint, text, callback)
      }, text ? SEARCH_DEBOUNCE_MS : 0)
    }
  }

  performSearch(endpoint, text, callback) {
    if (this.searchControllers[endpoint]) {
      this.searchControllers[endpoint].abort()
    }

    let abortController = new AbortController()
    this.searchControllers[endpoint] = abortController

    fetch(`${endpoint}?query=${encodeURIComponent(text)}`, { signal: abortController.signal })
      .then(response => {
        if (!response.ok) throw new Error("Mention search request failed");
        return response.json();
      })
      .then(results => callback(results))
      .catch(error => {
        if (error.name === "AbortError") return;
        callback([]);
        this.notifyMentionSearchUnavailable();
      })
      .finally(() => {
        if (this.searchControllers[endpoint] === abortController) {
          delete this.searchControllers[endpoint]
        }
      })
  }

  notifyMentionSearchUnavailable() {
    if (Date.now() - this.lastMentionSearchErrorAt < 5000) return;
    this.lastMentionSearchErrorAt = Date.now();

    if (window.notyf) {
      window.notyf.error("Mention search is temporarily unavailable. Please try again in a moment.");
    }
  }

  _pasteHtml(html, startPos, endPos) {
    let range = this.editor.getSelectedRange()
    let position = range[0]
    let length = endPos - startPos

    this.editor.setSelectedRange([position - length, position])
    this.editor.deleteInDirection("backward")
  }

  replaced(e) {
    let mention = e.detail.item.original
    let attachment = new Trix.Attachment({
      sgid: mention.sgid,
      content: mention.content
    })
    this.editor.insertAttachment(attachment)
    this.editor.insertString(" ")
  }
}